import { useEffect, useState, useRef } from 'react';
import Hero from './components/Hero';
import WhatWeDo from './components/WhatWeDo';
import HowItWorks from './components/HowItWorks';
import WhyItWorks from './components/WhyItWorks';
import Testimonials from './components/Testimonials';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-black text-white">
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://static.readdy.ai/image/7302b421718742a693240adfeb05edc7/5b9e3cd7f2338041e3bb6bb21e39ce32.jpeg" alt="Nexora Corp" className="h-20 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <a href="#services" onClick={(e) => handleSmoothScroll(e, 'services')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Services</a>
            <a href="#process" onClick={(e) => handleSmoothScroll(e, 'process')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Process</a>
            <a href="#contact" onClick={(e) => handleSmoothScroll(e, 'contact')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Contact</a>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://calendly.com/nexoracorp/30min" target="_blank" rel="noopener noreferrer" className="bg-[#0a1628] hover:bg-[#0d1e38] border border-[#1e3a8a]/50 text-white px-6 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-[#1e3a8a]/30 whitespace-nowrap cursor-pointer text-sm font-medium">Book a Demo</a>
          </div>
        </div>
      </nav>

      <Hero />
      <AnimatedSection>
        <WhatWeDo />
      </AnimatedSection>
      <AnimatedSection delay={100}>
        <HowItWorks />
      </AnimatedSection>
      <AnimatedSection delay={150}>
        <WhyItWorks />
      </AnimatedSection>
      <AnimatedSection delay={100}>
        <Testimonials />
      </AnimatedSection>
      <AnimatedSection delay={50}>
        <CallToAction />
      </AnimatedSection>
      <Footer />
    </div>
  );
}

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={sectionRef}
      className={`transition-all duration-700 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
    >
      {children}
    </div>
  );
}
